const STORAGE_KEY = 'crafty:wishlist:v1';
const MAX_ITEMS = 50;
const ACCOUNT_TOKEN_KEY = 'crafty:wishlist:account-token';
const ACCOUNT_OAUTH_KEY = 'crafty:wishlist:oauth';
const ACCOUNT_NAMESPACE = 'custom';
const ACCOUNT_METAFIELD_KEY = 'wishlist';
const KLAVIYO_PUBLIC_KEY = 'S2bS3K';
const KLAVIYO_REVISION = '2026-07-15';
let accountContext = null;
let accountSyncTimer = null;

(() => {
  const IDENTITY_EVENT = 'crafty_identity_ready';
  let pendingIdentity = null;
  let lastPublishedIdentity = '';

  const normalizeEmail = (value) => {
    if (typeof value !== 'string') return '';
    const email = value.trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
  };

  const hasRequiredConsent = () => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return false;

    return privacy.marketingAllowed() === true
      && privacy.analyticsProcessingAllowed() === true;
  };

  const publishIdentity = ({ email, source }) => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return;

    pendingIdentity = { email: normalizedEmail, source };
    if (!hasRequiredConsent()) return;

    const identityKey = `${source}:${normalizedEmail}`;
    if (identityKey === lastPublishedIdentity) {
      pendingIdentity = null;
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: IDENTITY_EVENT,
      identity_source: source,
      customer_info: {
        email: normalizedEmail
      },
      user_data: {
        email_address: normalizedEmail
      }
    });

    lastPublishedIdentity = identityKey;
    pendingIdentity = null;
  };

  window.addEventListener('crafty:identity:available', (event) => {
    publishIdentity({
      email: event.detail?.email,
      source: event.detail?.source || 'customer_account'
    });
  });

  window.addEventListener('klaviyoForms', (event) => {
    const detail = event.detail;
    if (!detail || (detail.type !== 'submit' && detail.type !== 'stepSubmit')) return;

    const metadata = detail.metaData || {};
    publishIdentity({
      email: metadata.email || metadata.$email,
      source: 'klaviyo_signup'
    });
  });

  document.addEventListener('visitorConsentCollected', () => {
    if (pendingIdentity) publishIdentity(pendingIdentity);
  });
})();

const safeParse = (value, fallback = []) => {
  try { return JSON.parse(value) ?? fallback; } catch (_) { return fallback; }
};

const read = () => {
  const items = safeParse(localStorage.getItem(STORAGE_KEY));
  return Array.isArray(items) ? items.slice(0, MAX_ITEMS) : [];
};

const write = (items, { sync = true } = {}) => {
  const unique = Array.from(new Map(items.map((item) => [String(item.id), item])).values()).slice(0, MAX_ITEMS);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(unique));
  window.dispatchEvent(new CustomEvent('crafty:wishlist:changed', { detail: { items: unique } }));
  if (sync) queueAccountSync(unique);
  return unique;
};

const randomString = (length = 64) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values, (value) => alphabet[value % alphabet.length]).join('');
};

const base64Url = (buffer) => btoa(String.fromCharCode(...new Uint8Array(buffer)))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const codeChallenge = async (verifier) => base64Url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));

const fetchDiscovery = async () => {
  const [openidResponse, apiResponse] = await Promise.all([
    fetch('/.well-known/openid-configuration', { credentials: 'same-origin' }),
    fetch('/.well-known/customer-account-api', { credentials: 'same-origin' })
  ]);
  if (!openidResponse.ok || !apiResponse.ok) throw new Error('Customer Account discovery failed');
  return { auth: await openidResponse.json(), api: await apiResponse.json() };
};

const saveToken = (token) => {
  const stored = { ...token, expires_at: Date.now() + Math.max(0, Number(token.expires_in || 0) - 60) * 1000 };
  sessionStorage.setItem(ACCOUNT_TOKEN_KEY, JSON.stringify(stored));
  return stored;
};

const readToken = () => {
  const token = safeParse(sessionStorage.getItem(ACCOUNT_TOKEN_KEY), null);
  return token?.access_token && token.expires_at > Date.now() ? token : null;
};

const setSyncStatus = (root, message, state = '') => {
  const status = root?.querySelector('[data-wishlist-sync-status]');
  if (!status) return;
  status.textContent = message;
  status.dataset.state = state;
};

const accountRequest = async (query, variables = {}) => {
  const token = readToken();
  if (!token || !accountContext?.graphqlEndpoint) throw new Error('Customer is not authenticated');
  const response = await fetch(accountContext.graphqlEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token.access_token },
    body: JSON.stringify({ query, variables })
  });
  if (response.status === 401) sessionStorage.removeItem(ACCOUNT_TOKEN_KEY);
  if (!response.ok) throw new Error(`Customer Account request failed (${response.status})`);
  const result = await response.json();
  if (result.errors?.length) throw new Error(result.errors[0].message);
  return result.data;
};

const WISHLIST_QUERY = `query WishlistLoad {
  customer {
    id
    emailAddress { emailAddress }
    wishlist: metafield(namespace: "${ACCOUNT_NAMESPACE}", key: "${ACCOUNT_METAFIELD_KEY}") { jsonValue }
  }
}`;

const WISHLIST_MUTATION = `mutation WishlistSave($metafields: [MetafieldsSetInput!]!) {
  metafieldsSet(metafields: $metafields) {
    metafields { key namespace jsonValue updatedAt }
    userErrors { field message code }
  }
}`;

const mergeWishlists = (localItems, accountItems) => {
  const merged = new Map();
  [...accountItems, ...localItems].forEach((item) => {
    const id = String(item.id);
    const saved = merged.get(id);
    if (!saved || Number(item.savedAt || 0) >= Number(saved.savedAt || 0)) merged.set(id, item);
  });
  return Array.from(merged.values())
    .sort((a, b) => Number(b.savedAt || 0) - Number(a.savedAt || 0))
    .slice(0, MAX_ITEMS);
};

const saveAccountWishlist = async (items) => {
  if (!accountContext?.customerId || !readToken()) return;
  const data = await accountRequest(WISHLIST_MUTATION, {
    metafields: [{
      ownerId: accountContext.customerId,
      namespace: ACCOUNT_NAMESPACE,
      key: ACCOUNT_METAFIELD_KEY,
      type: 'json',
      value: JSON.stringify(items)
    }]
  });
  const error = data.metafieldsSet.userErrors?.[0];
  if (error) throw new Error(error.message);
};

const queueAccountSync = (items) => {
  if (!accountContext?.customerId || !readToken()) return;
  clearTimeout(accountSyncTimer);
  accountSyncTimer = setTimeout(async () => {
    try {
      setSyncStatus(accountContext.root, 'Syncing your wishlist…', 'syncing');
      await saveAccountWishlist(items);
      setSyncStatus(accountContext.root, 'Wishlist saved to your account', 'synced');
    } catch (_) {
      setSyncStatus(accountContext.root, 'Saved on this device. Account sync will retry.', 'error');
    }
  }, 650);
};

const synchronizeAccount = async (root) => {
  const data = await accountRequest(WISHLIST_QUERY);
  const accountItems = Array.isArray(data.customer.wishlist?.jsonValue) ? data.customer.wishlist.jsonValue : [];
  const merged = mergeWishlists(read(), accountItems);
  const customerEmail = data.customer.emailAddress?.emailAddress || '';
  accountContext.customerId = data.customer.id;
  write(merged, { sync: false });
  renderPage(root);
  const email = root.querySelector('#WishlistEmail');
  if (email && !email.value) email.value = customerEmail;
  if (customerEmail) {
    window.dispatchEvent(new CustomEvent('crafty:identity:available', {
      detail: { email: customerEmail, source: 'wishlist_login' }
    }));
  }
  await saveAccountWishlist(merged);
  const login = root.querySelector('[data-wishlist-account-login]');
  if (login) login.textContent = 'Wishlist synced';
  setSyncStatus(root, 'Saved to your account and available on your devices', 'synced');
};

const exchangeAuthorizationCode = async (root, discovery) => {
  const params = new URLSearchParams(location.search);
  const code = params.get('code');
  if (!code) return false;
  const oauth = safeParse(sessionStorage.getItem(ACCOUNT_OAUTH_KEY), null);
  if (!oauth || params.get('state') !== oauth.state) throw new Error('Invalid Customer Account state');
  const response = await fetch(discovery.auth.token_endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: root.dataset.customerApiClientId,
      redirect_uri: root.dataset.customerApiRedirectUri,
      code,
      code_verifier: oauth.verifier
    })
  });
  if (!response.ok) throw new Error('Customer Account sign-in failed');
  saveToken(await response.json());
  sessionStorage.removeItem(ACCOUNT_OAUTH_KEY);
  params.delete('code'); params.delete('state'); params.delete('shop');
  const cleanQuery = params.toString();
  history.replaceState({}, '', `${location.pathname}${cleanQuery ? `?${cleanQuery}` : ''}`);
  return true;
};

const beginAccountLogin = async (root) => {
  setSyncStatus(root, 'Opening secure Shopify sign-in…', 'syncing');
  const discovery = await fetchDiscovery();
  const verifier = randomString(72);
  const state = randomString(32);
  const nonce = randomString(32);
  sessionStorage.setItem(ACCOUNT_OAUTH_KEY, JSON.stringify({ verifier, state, nonce }));
  const url = new URL(discovery.auth.authorization_endpoint);
  url.search = new URLSearchParams({
    scope: 'openid email customer-account-api:full',
    client_id: root.dataset.customerApiClientId,
    response_type: 'code',
    redirect_uri: root.dataset.customerApiRedirectUri,
    state,
    nonce,
    code_challenge: await codeChallenge(verifier),
    code_challenge_method: 'S256'
  });
  location.assign(url.toString());
};

const initializeAccount = async (root) => {
  if (!root.dataset.customerApiClientId || !root.dataset.customerApiRedirectUri) return;
  const login = root.querySelector('[data-wishlist-account-login]');
  login?.addEventListener('click', () => beginAccountLogin(root).catch(() => setSyncStatus(root, 'Unable to open sign-in. Please try again.', 'error')));
  const hasCallback = new URLSearchParams(location.search).has('code');
  if (!hasCallback && !readToken()) return;
  try {
    setSyncStatus(root, 'Connecting your wishlist…', 'syncing');
    const discovery = await fetchDiscovery();
    accountContext = { root, graphqlEndpoint: discovery.api.graphql_api, customerId: null };
    if (hasCallback) await exchangeAuthorizationCode(root, discovery);
    await synchronizeAccount(root);
  } catch (_) {
    sessionStorage.removeItem(ACCOUNT_TOKEN_KEY);
    setSyncStatus(root, 'Your wishlist is still saved on this device. Sign in to retry sync.', 'error');
  }
};

const track = (event, item = {}, extra = {}) => {
  const eventId = `wishlist_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const value = Number(item.priceRaw || 0) / 100;
  const currency = window.Shopify?.currency?.active || 'USD';
  const payload = {
    event,
    event_id: eventId,
    ecommerce: {
      currency,
      value: item.id ? value : 0,
      items: item.id ? [{ item_id: String(item.id), item_name: item.title, price: value, quantity: 1 }] : []
    },
    ...extra
  };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  if (event === 'add_to_wishlist' && typeof window.fbq === 'function') {
    window.fbq('track', 'AddToWishlist', { content_ids: [String(item.id)], content_name: item.title, content_type: 'product', value, currency }, { eventID: eventId });
  }
};

const itemFromButton = (button) => ({
  id: button.dataset.productId,
  handle: button.dataset.productHandle,
  url: button.dataset.productUrl,
  title: button.dataset.productTitle,
  image: button.dataset.productImage,
  price: button.dataset.productPrice,
  priceRaw: button.dataset.productPriceRaw,
  compareAt: button.dataset.productCompareAt || '',
  savedAt: Date.now()
});

const syncUI = (items = read()) => {
  const ids = new Set(items.map((item) => String(item.id)));
  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    const active = ids.has(String(button.dataset.productId));
    button.setAttribute('aria-pressed', String(active));
    const label = button.querySelector('[data-wishlist-label]');
    if (label) label.textContent = active ? button.dataset.removeLabel : button.dataset.saveLabel;
  });
  document.querySelectorAll('[data-wishlist-count]').forEach((counter) => {
    counter.dataset.count = String(items.length);
    const count = counter.querySelector('.cart-bubble__text-count');
    if (count) count.textContent = String(items.length);
  });
};

const toast = (message) => {
  let node = document.querySelector('.crafty-wishlist-toast');
  if (!node) {
    node = document.createElement('div');
    node.className = 'crafty-wishlist-toast';
    node.setAttribute('role', 'status');
    document.body.append(node);
  }
  node.textContent = message;
  node.classList.add('is-visible');
  clearTimeout(toast.timer);
  toast.timer = setTimeout(() => node.classList.remove('is-visible'), 1800);
};

const toggle = (button) => {
  const item = itemFromButton(button);
  const items = read();
  const index = items.findIndex((saved) => String(saved.id) === String(item.id));
  if (index >= 0) {
    items.splice(index, 1);
    write(items);
    track('remove_from_wishlist', item);
    toast(button.dataset.removedMessage || 'Removed from your wishlist');
  } else {
    if (items.length >= MAX_ITEMS) {
      toast(button.dataset.limitMessage || 'Your wishlist can hold up to 50 products');
      return;
    }
    write([item, ...items]);
    track('add_to_wishlist', item);
    toast(button.dataset.addedMessage || 'Saved to your wishlist');
  }
};

const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));

const shareToken = (items) => {
  const data = items.map(({ id, handle, url, title, image, price, priceRaw, compareAt }) => ({ id, handle, url, title, image, price, priceRaw, compareAt }));
  return btoa(unescape(encodeURIComponent(JSON.stringify(data)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const wishlistValue = (items) => items.reduce((total, item) => {
  const cents = Number.parseInt(item.priceRaw, 10);
  return total + (Number.isFinite(cents) ? cents / 100 : 0);
}, 0);

const sendWishlistEmailEvent = async (email, items, restoreUrl) => {
  const products = items.map(({ id, title, url, image, price, priceRaw }) => ({
    product_id: String(id),
    title,
    url: new URL(url, location.origin).href,
    image,
    price,
    price_value: Number.isFinite(Number.parseInt(priceRaw, 10)) ? Number.parseInt(priceRaw, 10) / 100 : 0
  }));
  const uniqueId = crypto.randomUUID?.() || `wishlist-${Date.now()}-${randomString(16)}`;
  const response = await fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(KLAVIYO_PUBLIC_KEY)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      revision: KLAVIYO_REVISION
    },
    body: JSON.stringify({
      data: {
        type: 'event',
        attributes: {
          properties: {
            restore_url: restoreUrl,
            item_count: items.length,
            product_ids: items.map((item) => String(item.id)),
            products
          },
          metric: {
            data: {
              type: 'metric',
              attributes: { name: 'Wishlist Email Requested' }
            }
          },
          profile: {
            data: {
              type: 'profile',
              attributes: { email }
            }
          },
          value: wishlistValue(items),
          unique_id: uniqueId
        }
      }
    })
  });
  if (!response.ok) throw new Error(`Klaviyo event failed (${response.status})`);
};

const restoreFromUrl = () => {
  const token = new URLSearchParams(location.search).get('list');
  if (!token) return false;
  try {
    const normalized = token.replace(/-/g, '+').replace(/_/g, '/');
    const restored = JSON.parse(decodeURIComponent(escape(atob(normalized))));
    if (!Array.isArray(restored)) return false;
    write([...restored.map((item) => ({ ...item, savedAt: Date.now() })), ...read()]);
    history.replaceState({}, '', location.pathname);
    track('wishlist_restored', {}, { item_count: restored.length });
    return true;
  } catch (_) { return false; }
};

const renderPage = (root) => {
  const grid = root.querySelector('[data-wishlist-grid]');
  const empty = root.querySelector('[data-wishlist-empty]');
  const count = root.querySelector('[data-wishlist-page-count]');
  if (!grid || !empty) return;
  const items = read();
  count.textContent = String(items.length);
  empty.hidden = items.length > 0;
  grid.hidden = items.length === 0;
  grid.innerHTML = items.map((item) => `
    <article class="crafty-wishlist-card" data-wishlist-card="${escapeHtml(item.id)}">
      <a class="crafty-wishlist-card__image-link" href="${escapeHtml(item.url)}" data-wishlist-product-link data-product-id="${escapeHtml(item.id)}">
        <img class="crafty-wishlist-card__image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" width="720" height="720">
      </a>
      <button type="button" class="crafty-wishlist-button crafty-wishlist-card__remove" data-wishlist-remove="${escapeHtml(item.id)}" aria-pressed="true" aria-label="${escapeHtml(root.dataset.removeLabel)}">
        <span class="crafty-wishlist-button__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"/></svg></span>
      </button>
      <div class="crafty-wishlist-card__content">
        <a class="crafty-wishlist-card__title" href="${escapeHtml(item.url)}" data-wishlist-product-link data-product-id="${escapeHtml(item.id)}">${escapeHtml(item.title)}</a>
        <p class="crafty-wishlist-card__price">${escapeHtml(item.price)}${item.compareAt ? `<span class="crafty-wishlist-card__compare">${escapeHtml(item.compareAt)}</span>` : ''}</p>
      </div>
    </article>`).join('');
};

const initializePage = () => {
  const root = document.querySelector('[data-wishlist-page]');
  if (!root) return;
  const restored = restoreFromUrl();
  renderPage(root);
  track('wishlist_opened', {}, { item_count: read().length, restored });
  root.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-wishlist-remove]');
    if (remove) {
      const items = read();
      const item = items.find((saved) => String(saved.id) === remove.dataset.wishlistRemove);
      write(items.filter((saved) => String(saved.id) !== remove.dataset.wishlistRemove));
      track('remove_from_wishlist', item || {});
      renderPage(root);
      return;
    }
    const productLink = event.target.closest('[data-wishlist-product-link]');
    if (productLink) track('wishlist_product_click', { id: productLink.dataset.productId });
  });
  root.querySelector('[data-wishlist-email-open]')?.addEventListener('click', () => root.querySelector('[data-wishlist-email-dialog]')?.showModal());
  root.querySelector('[data-wishlist-email-close]')?.addEventListener('click', () => root.querySelector('[data-wishlist-email-dialog]')?.close());
  root.querySelector('[data-wishlist-email-form]')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const submit = form.querySelector('[type="submit"]');
    const email = String(new FormData(form).get('email') || '').trim();
    const items = read();
    const restoreUrl = `${location.origin}${location.pathname}?list=${shareToken(items)}`;
    if (!email || !items.length || submit?.disabled) return;
    if (submit) submit.disabled = true;
    try {
      await sendWishlistEmailEvent(email, items, restoreUrl);
      window.dispatchEvent(new CustomEvent('crafty:identity:available', {
        detail: { email, source: 'wishlist_email' }
      }));
      track('wishlist_email_requested', {}, { item_count: items.length });
      form.closest('dialog')?.close();
      toast(root.dataset.emailSuccess);
    } catch (error) {
      console.warn('[Crafty Wishlist] Email request could not be sent.', error);
      toast('We could not send your wishlist. Please try again.');
    } finally {
      if (submit) submit.disabled = false;
    }
  });
};

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wishlist-toggle]');
  if (!button) return;
  event.preventDefault();
  event.stopPropagation();
  toggle(button);
});

document.addEventListener('DOMContentLoaded', () => {
  syncUI();
  initializePage();
  const root = document.querySelector('[data-wishlist-page]');
  if (root) initializeAccount(root);
});
window.addEventListener('crafty:wishlist:changed', (event) => syncUI(event.detail.items));
window.addEventListener('pageshow', () => syncUI());
